/**
 * 群组 store（v3.9）：会话级群列表 + 未读 + 摘要。
 * 持久化由 GroupService 负责（localStorage `mesh_groups_v1`）；本 store 只管渲染态。
 */
import { create } from 'zustand';
import type { MeshGroupLocal } from '@shared/types';

const GROUP_STORAGE_KEY = 'mesh_groups_v1';

export function loadGroups(): MeshGroupLocal[] {
  try {
    const raw = localStorage.getItem(GROUP_STORAGE_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw) as unknown[];
    if (!Array.isArray(list)) return [];
    return list
      .filter((g) => g && typeof g === 'object' && typeof (g as MeshGroupLocal).id === 'string')
      .map((g) => ({
        ...(g as MeshGroupLocal),
        members: Array.isArray((g as MeshGroupLocal).members) ? (g as MeshGroupLocal).members : [],
        unread: typeof (g as MeshGroupLocal).unread === 'number' ? (g as MeshGroupLocal).unread : 0,
        muted: !!(g as MeshGroupLocal).muted,
      }));
  } catch {
    return [];
  }
}

export function persistGroups(groups: MeshGroupLocal[]): void {
  try {
    localStorage.setItem(GROUP_STORAGE_KEY, JSON.stringify(groups));
  } catch {
    /* 存储满/禁用时静默 */
  }
}

interface GroupState {
  groups: MeshGroupLocal[];
  activeGroupId: string | null;
  groupUnreadTotal: number;
  setGroups(groups: MeshGroupLocal[]): void;
  upsert(g: MeshGroupLocal): void;
  remove(groupId: string): void;
  setActive(groupId: string | null): void;
  /** 群摘要更新（lastText/lastTs）+ 未读累加；activeGroup 打开时不加未读。 */
  touch(groupId: string, patch: Partial<MeshGroupLocal>, delta: number): void;
  markRead(groupId: string): void;
  /** 发群消息后本地回显（out 消息不产生未读）。 */
  echo(groupId: string, lastText: string, ts: number): void;
}

export const useGroupStore = create<GroupState>((set, get) => ({
  groups: [],
  activeGroupId: null,
  groupUnreadTotal: 0,

  setGroups(groups) {
    set({
      groups,
      groupUnreadTotal: groups.reduce((s, g) => s + g.unread, 0),
    });
  },

  upsert(g) {
    const groups = [...get().groups];
    const i = groups.findIndex((x) => x.id === g.id);
    if (i >= 0) groups[i] = { ...groups[i], ...g };
    else groups.unshift(g);
    set({ groups });
    persistGroups(groups);
  },

  remove(groupId) {
    const groups = get().groups.filter((g) => g.id !== groupId);
    set({
      groups,
      groupUnreadTotal: groups.reduce((s, g) => s + g.unread, 0),
      activeGroupId: get().activeGroupId === groupId ? null : get().activeGroupId,
    });
    persistGroups(groups);
  },

  setActive(groupId) {
    set({ activeGroupId: groupId });
  },

  touch(groupId, patch, delta) {
    const groups = get().groups.map((g) =>
      g.id === groupId ? { ...g, ...patch, unread: g.unread + delta } : g,
    );
    set({
      groups,
      groupUnreadTotal: groups.reduce((s, g) => s + g.unread, 0),
    });
    persistGroups(groups);
  },

  markRead(groupId) {
    const groups = get().groups.map((g) => (g.id === groupId ? { ...g, unread: 0 } : g));
    set({
      groups,
      groupUnreadTotal: groups.reduce((s, g) => s + g.unread, 0),
    });
    persistGroups(groups);
  },

  echo(groupId, lastText, ts) {
    const groups = get().groups.map((g) => (g.id === groupId ? { ...g, lastText, lastTs: ts } : g));
    set({ groups });
    persistGroups(groups);
  },
}));
